import React from 'react'
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js'
import { ConfirmCardPaymentData, PaymentIntent } from '@stripe/stripe-js'
import styled from '@emotion/styled'
import { Colors, Shapes } from 'src/components/theme'
import { Spinner } from 'src/components/spinner'
import { Typography } from '@mui/material'
import dynamic from 'next/dynamic'
import { CreditCardForm } from 'src/components/credit-card-form'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { SubscriptionModalFooter } from 'src/views/subscriptions/footer'

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Shapes.spacing[2]}px;
  margin-top: ${Shapes.spacing[4]}px;
`

const Spacer = styled.div`
  flex: 1;
`

type CheckoutFormProps = {
  clientSecret: string
  payTitle?: string
  onSuccess?: (paymentIntent: PaymentIntent | undefined) => Promise<void>
  onError?: () => void
  error?: string
  onBack: () => void
}
const CheckoutForm = (props: CheckoutFormProps) => {
  const { payTitle, clientSecret, onSuccess, onError, error, onBack } = props
  const stripe = useStripe()
  const elements = useElements()

  const [message, setMessage] = React.useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = React.useState(false)

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      return
    }

    setIsLoading(true)
    setMessage(undefined)

    // Set up payment method for recurring usage
    const cardNumberElement = elements.getElement(CardNumberElement)
    const paymentMethod = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement as any,
    })

    if (!paymentMethod || paymentMethod.error) {
      setMessage('Something went wrong.')
      setIsLoading(false)
      return
    }

    const data: ConfirmCardPaymentData = {
      payment_method: paymentMethod?.paymentMethod?.id,
    }

    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, data)

    if (error) {
      setMessage(error.message ?? 'An unexpected error occurred.')
      onError?.()
    }

    if (paymentIntent?.status === 'succeeded') {
      await onSuccess?.(paymentIntent)
    }
    setIsLoading(false)
  }

  const renderBottomContent = () => (
    <>
      <MessageWrapper>
        {message && (
          <Typography variant="text1m" color={Colors.redDark}>
            {message}
          </Typography>
        )}
        {error && (
          <Typography variant="text1m" color={Colors.redDark}>
            {error}
          </Typography>
        )}
        <Typography marginTop={`${Shapes.spacing[2]}px`} variant="textmdm" color={Colors.textLight}>
          By providing your card information, you allow AlphaMap to charge your card for future payments in accordance
          with their terms.
        </Typography>
      </MessageWrapper>
      <Spacer />
      <SubscriptionModalFooter>
        <ButtonWithIcon onClick={onBack}>Back</ButtonWithIcon>
        <ButtonWithIcon type="submit" inverted={!!payTitle} disabled={isLoading || !stripe || !elements || !payTitle}>
          {isLoading ? (
            <Spinner
              style={{
                color: `${Colors.textWhite}`,
              }}
              size={16}
            />
          ) : (
            payTitle || 'Pay'
          )}
        </ButtonWithIcon>
      </SubscriptionModalFooter>
    </>
  )

  return <CreditCardForm bottomContent={renderBottomContent()} onSubmit={handleSubmit} />
}

export default dynamic(() => Promise.resolve(CheckoutForm), {
  ssr: false,
})
